import { useState, useEffect } from 'react';

export type VariantType =
  | 'native' // ConfigCat native variant
  | 'custom'; // custom variant for analytics purpose

export type FeatureFlagMetadata = 'featureFlag';
export type DynamicStringFeatureMetadata = 'dynamic';
export type PossibleVariantsMetadata = {
  control: 'native';
  [variantName: string]: VariantType;
};

export enum Target {
  NEW_USERS = 'newUsers',
  ALL_USERS = 'allUsers',
  // Add other user targets here in the future, e.g., PREMIUM_USERS = 'premiumUsers'
}

// Custom variant for excluding users that are not the target of the feature
// e.g., if the feature is only for new users, then we can exclude existing users, and this will not trigger analytics logging
// if you need to log the "excluded" users, please use `customVariants` via `.withCustomVariants()` and `customVariantsMapper` on the function.
export type ExcludedNotLoggedVariant = 'excludedNotLogged';

export type MultiVariantsFeatureMetadata = {
  target: Target;
  possibleVariants: PossibleVariantsMetadata;
};

export type FeatureMetadata = FeatureFlagMetadata | DynamicStringFeatureMetadata | MultiVariantsFeatureMetadata;

export type FeatureDefinitions = Readonly<{
  [key: string]: FeatureMetadata;
}>;

export interface CustomVariantMapper<T> {
  variant: T;
  condition: () => Promise<boolean>;
}

export type OptionsToDisableAutoLogVariant = {
  shouldDisableAutoLogVariant?: string;
};

export type Options<VariantType> = OptionsToDisableAutoLogVariant & {
  defaultVariant?: VariantType;
};

/**
 * A helper/utility function that allows client to create a feature definition object with type safety in another file,
 *  so that the feature definition doesn't have to be created as an inline object in the same file as the feature library.
 * @param definitions
 * @returns
 */
export const createFeatureDefinitions = <P extends FeatureDefinitions>(definitions: P) => definitions;

export const createFeatureFlag = () => 'featureFlag' as const;
export const createDynamicStringFeature = () => 'dynamic' as const;
type NativeVariantMap<Keys extends string> = {
  [K in Keys]: 'native';
};

type CustomVariantMap<Keys extends string> = {
  [K in Keys]: 'custom';
};

class InitialBuilder {
  standardAbTest(): IntermediateBuilder<'control' | 'experiment', Target.ALL_USERS, never> {
    return this.withVariants('control', 'experiment');
  }

  withVariants<NewKeys extends string>(
    ...newVariants: NewKeys[]
  ): IntermediateBuilder<NewKeys, Target.ALL_USERS, never> {
    return new IntermediateBuilder<NewKeys, Target.ALL_USERS, never>(...newVariants);
  }
}

class IntermediateBuilder<NativeKeys extends string, Targeting extends Target, CustomKeys extends string = never> {
  private nativeVariants: NativeVariantMap<NativeKeys>;
  private customVariants: CustomVariantMap<CustomKeys> = {} as CustomVariantMap<CustomKeys>;
  private target: Targeting;

  constructor(...nativeVariants: NativeKeys[]) {
    this.nativeVariants = {} as NativeVariantMap<NativeKeys>;
    nativeVariants.forEach((variant) => {
      this.nativeVariants[variant] = 'native';
    });
    this.target = Target.ALL_USERS as Targeting; // force the type here as we do downcast below
  }

  withCustomVariants<NewCustomKeys extends string>(
    ...newCustomVariants: NewCustomKeys[]
  ): IntermediateBuilder<NativeKeys, Targeting, CustomKeys | NewCustomKeys> {
    newCustomVariants.forEach((variant) => {
      (this.customVariants as CustomVariantMap<CustomKeys | NewCustomKeys>)[variant] = 'custom';
    });
    return this as IntermediateBuilder<NativeKeys, Targeting, CustomKeys | NewCustomKeys>;
  }

  targetingOnlyNewUsers(): IntermediateBuilder<NativeKeys, Target.NEW_USERS, CustomKeys> {
    this.target = Target.NEW_USERS as Targeting; // force the type here as we do downcast below
    return this as IntermediateBuilder<NativeKeys, Target.NEW_USERS, CustomKeys>;
  }

  build(): {
    target: Targeting;
    possibleVariants: NativeVariantMap<NativeKeys> & CustomVariantMap<Exclude<CustomKeys, never>>;
  } {
    return {
      target: this.target,
      possibleVariants: { ...this.nativeVariants, ...this.customVariants },
    };
  }
}

export const createMultiVariantsTestFeature = () => new InitialBuilder();

/**
 *
 * @param featureDefinitions - an object defining all the feature config and their variants/variants types
 * @param dependencies - an object containing all the dependencies needed for the feature library to work
 * @returns
 */
export function createFeatureLibrary<FD extends FeatureDefinitions>(
  featureDefinitions: FD,
  {
    client,
    getBrowserMetadata,
    logVariant,
    logFaroEvent,
    loadFeatureFlags,
  }: {
    client: string; // 'web' | 'chromeExtension' | 'foyer' but no need to be exhaustive yet for now
    getBrowserMetadata: () => {
      browserName: string;
      browserVersion: string;
      hostname?: string;
      href?: string;
    };
    logVariant: (featureKey: string, variant: string) => void;
    logFaroEvent: (name: string, attributes?: Record<string, string>, domain?: string) => void;
    loadFeatureFlags: () => Promise<Record<string, any>>;
  },
) {
  type AllFeatureKeys = keyof FD;

  type FeatureKeysTargetingNewUsers = {
    [K in AllFeatureKeys]: FD[K] extends MultiVariantsFeatureMetadata
      ? FD[K]['target'] extends Target.NEW_USERS
        ? K
        : never
      : never;
  }[keyof FD];

  type CollectVariants<M extends PossibleVariantsMetadata, Target extends 'custom' | 'native'> = {
    [K in keyof M]: M[K] extends Target ? K : never;
  }[keyof M];
  type CustomVariants<K extends keyof FD> = FD[K] extends MultiVariantsFeatureMetadata
    ? CollectVariants<FD[K]['possibleVariants'], 'custom'>
    : never;
  type NativeVariants<K extends keyof FD> = FD[K] extends MultiVariantsFeatureMetadata
    ? CollectVariants<FD[K]['possibleVariants'], 'native'>
    : never;

  type FeatureKeysWithNativeVariants = {
    [K in AllFeatureKeys]: NativeVariants<K> extends never ? never : K;
  }[keyof FD];

  type FeatureKeysWithCustomVariants = {
    [K in AllFeatureKeys]: CustomVariants<K> extends never ? never : K;
  }[keyof FD];

  type FeatureKeysWithNativeAndCustomVariants = FeatureKeysWithNativeVariants & FeatureKeysWithCustomVariants;

  type FeatureFlagKeys = {
    [K in AllFeatureKeys]: FD[K] extends FeatureFlagMetadata ? K : never;
  }[keyof FD];

  type DynamicStringFeatureKeys = {
    [K in AllFeatureKeys]: FD[K] extends DynamicStringFeatureMetadata ? K : never;
  }[keyof FD];

  type FeatureKeysWithOnlyNativeVariants = Exclude<
    FeatureKeysWithNativeVariants,
    FeatureKeysWithNativeAndCustomVariants
  >;

  type FeatureKeysWithOnlyCustomVariants = Exclude<
    FeatureKeysWithCustomVariants,
    FeatureKeysWithNativeAndCustomVariants
  >;

  type AllVariantsOf<K extends AllFeatureKeys> = K extends FeatureKeysWithNativeAndCustomVariants
    ? CustomVariants<K> | NativeVariants<K> //
    : K extends FeatureKeysWithOnlyNativeVariants
    ? NativeVariants<K>
    : K extends FeatureFlagKeys
    ? boolean //
    : K extends DynamicStringFeatureKeys
    ? string //
    : never;

  type OptionsType<K extends AllFeatureKeys> = K extends FeatureKeysWithNativeAndCustomVariants
    ? OptionsWithCustomVariantMapper<K>
    : Options<AllVariantsOf<K>>;

  type OptionsWithCustomVariantMapper<K extends AllFeatureKeys> = Options<AllVariantsOf<K>> & {
    customVariantsMapper: CustomVariantMapper<CustomVariants<K>>[];
  };

  type UseFeatureVariantStillLoadingReturnType = {
    isLoading: true;
  };

  type UseFeatureVariantFinishedReturnType<K> = {
    isLoading: false;
    variant: K;
  };

  type UseFeatureVariantReturnType<K> =
    | UseFeatureVariantStillLoadingReturnType
    | UseFeatureVariantFinishedReturnType<K>;

  type WithoutFeatureKeysTargetingNewUsers<K extends AllFeatureKeys> = Exclude<K, FeatureKeysTargetingNewUsers>;

  type GetFeatureVariantFn = {
    // This signature is for keys that support custom variants. It requires mandatory options as we need to provide the customVariantMapper.
    <K extends WithoutFeatureKeysTargetingNewUsers<FeatureKeysWithNativeAndCustomVariants>>(
      key: K,
      options: OptionsType<K>,
    ): Promise<AllVariantsOf<K>>;

    // This signature is for other keys where options are optional.
    <K extends WithoutFeatureKeysTargetingNewUsers<Exclude<keyof FD, FeatureKeysWithNativeAndCustomVariants>>>(
      key: K,
      options?: OptionsType<K>,
    ): Promise<AllVariantsOf<K>>;
  };

  type UseFeatureVariantFn = {
    // This signature is for keys that support custom variants. It requires options.
    <K extends WithoutFeatureKeysTargetingNewUsers<FeatureKeysWithNativeAndCustomVariants>>(
      key: K,
      options: OptionsType<K>,
    ): UseFeatureVariantReturnType<AllVariantsOf<K>>;

    // This signature is for other keys where options are optional.
    <K extends WithoutFeatureKeysTargetingNewUsers<Exclude<keyof FD, FeatureKeysWithNativeAndCustomVariants>>>(
      key: K,
      options?: OptionsType<K>,
    ): UseFeatureVariantReturnType<AllVariantsOf<K>>;
  };

  const _shouldAutoLogVariant = <K extends AllFeatureKeys>(key: K, options: OptionsType<K>): boolean => {
    // We don't auto log dynamic string feature variants
    if (_isDynamicStringKey(key)) return false;

    // We only auto log variants if the option is not explicitly set to false
    const shouldDisable = 'shouldDisableAutoLogVariant' in options && Boolean(options.shouldDisableAutoLogVariant);
    return !shouldDisable;
  };

  const _isDynamicStringKey = (key: AllFeatureKeys): key is DynamicStringFeatureKeys => {
    return key in featureDefinitions && featureDefinitions[key] === 'dynamic';
  };

  const _isFeatureFlagKey = (key: AllFeatureKeys): key is FeatureFlagKeys => {
    return key in featureDefinitions && featureDefinitions[key] === 'featureFlag';
  };

  const _isFeatureKeyWithNativeVariants = (key: AllFeatureKeys): key is FeatureKeysWithNativeVariants => {
    if (
      !(key in featureDefinitions) ||
      !featureDefinitions[key] ||
      featureDefinitions[key] === 'featureFlag' ||
      featureDefinitions[key] === 'dynamic' ||
      typeof featureDefinitions[key] !== 'object' ||
      !('possibleVariants' in (featureDefinitions[key] as object))
    ) {
      return false;
    }
    const possibleVariants = (featureDefinitions[key] as MultiVariantsFeatureMetadata)['possibleVariants'];
    return Object.values(possibleVariants).includes('native');
  };

  const _getDefaultVariantFallback = <K extends AllFeatureKeys>(key: K): AllVariantsOf<K> => {
    if (_isDynamicStringKey(key)) {
      return '' as AllVariantsOf<K>;
    }
    if (_isFeatureFlagKey(key)) {
      return false as AllVariantsOf<K>;
    }
    return 'control' as AllVariantsOf<K>;
  };

  const _getDefaultVariant = <K extends AllFeatureKeys>(key: K, options?: OptionsType<K>): AllVariantsOf<K> => {
    if (options && 'defaultVariant' in options) {
      return options.defaultVariant as AllVariantsOf<K>;
    }
    return _getDefaultVariantFallback(key);
  };

  const _getAndLogVariant = async <K extends AllFeatureKeys>(key: K, options?: OptionsType<K>) => {
    options =
      options ??
      ({
        defaultVariant: _getDefaultVariantFallback(key),
        shouldDisableAutoLogVariant: false,
      } as unknown as OptionsType<K>);

    const defaultVariant = _getDefaultVariant(key, options);

    const logWarning = (message: string) => {
      logFaroEvent('lib-feature-flag-warning', {
        client,
        ...getBrowserMetadata(),
        featureKey: `${key as string}`,
        message,
      });
    };

    const _loadVariant = async () => {
      const allVariants = await loadFeatureFlags();

      if (!allVariants || typeof allVariants !== 'object') {
        throw new Error(
          `loadFeatureFlags returned: ${JSON.stringify(allVariants)} , which is not an expected Record<string, any>`,
        );
      }

      if (!((key as string) in allVariants)) {
        throw new Error(`No variant is found for feature key: ${key as string}`);
      }

      const variant = allVariants[key as string];

      if (_isFeatureFlagKey(key)) {
        if (typeof variant !== 'boolean')
          logWarning(
            `${
              key as string
            } is expected to return boolean flag, but the variant returned is not a boolean: ${variant}`,
          );
        return variant as boolean;
      }

      if (_isDynamicStringKey(key)) {
        if (typeof variant !== 'string')
          logWarning(
            `${key as string} is expected to be a dynamic string, but the variant returned is not a string: ${variant}`,
          );
        return variant as string;
      }

      if ((options as any).customVariantsMapper) {
        const overrideChecks = (options as unknown as OptionsWithCustomVariantMapper<typeof key>).customVariantsMapper;
        for (let i = 0; i < overrideChecks.length; i++) {
          if (await overrideChecks[i].condition()) {
            return overrideChecks[i].variant;
          }
        }
      }

      if (_isFeatureKeyWithNativeVariants(key)) {
        return variant as NativeVariants<typeof key>;
      }

      logWarning(
        `${key as string} is not a known feature key. Please check that the key is defined in the featureDefinitions`,
      );
      return variant;
    };

    const shouldAutoLogVariant = _shouldAutoLogVariant(key, options);

    try {
      const variant = await _loadVariant();

      if (shouldAutoLogVariant) {
        logVariant(key as string, variant as any);
      }

      if (typeof featureDefinitions[key] === 'object') {
        const variantMappings = (featureDefinitions[key] as MultiVariantsFeatureMetadata)['possibleVariants'] ?? {};
        const isKnownVariant = variant in variantMappings;
        if (!isKnownVariant) {
          logWarning(`Variant "${variant}" is not a known variant in featureMappings["${key as string}"]`);
        }
      }
      return variant;
    } catch (e) {
      // we don't want to log the variant when error happened because it's not a proper assignment (something is wrong),
      // and we only want to fallback to defaultVariant.
      logWarning(`Returning defaultVariant: ${String(defaultVariant)} because of error: ${e}`);
      return defaultVariant;
    }
  };

  const getFeatureVariant: GetFeatureVariantFn = async <K extends WithoutFeatureKeysTargetingNewUsers<AllFeatureKeys>>(
    key: K,
    options?: OptionsType<K>,
  ) => {
    return _getAndLogVariant(key, options);
  };

  const useFeatureVariant: UseFeatureVariantFn = <K extends WithoutFeatureKeysTargetingNewUsers<AllFeatureKeys>>(
    key: K,
    options?: OptionsType<K>,
  ) => {
    const [status, setStatus] = useState<UseFeatureVariantReturnType<AllVariantsOf<K>>>({ isLoading: true });

    useEffect(() => {
      _getAndLogVariant(key, options).then((variant: AllVariantsOf<K>) => {
        setStatus({ isLoading: false, variant });
      });
    }, [key]);

    return status;
  };

  const isFeatureVariantReady = <K extends keyof FD>(
    result: UseFeatureVariantReturnType<AllVariantsOf<K>>,
  ): result is UseFeatureVariantFinishedReturnType<AllVariantsOf<K>> => {
    return !result.isLoading;
  };

  const getFeatureVariantForNewUsers = async <K extends FeatureKeysTargetingNewUsers>(
    key: K,
    { isNewUser, ...options }: Options<AllVariantsOf<K>> & { isNewUser: () => Promise<boolean> },
  ): Promise<AllVariantsOf<K> | 'excludedNotLogged'> => {
    const featureMetadata = featureDefinitions[key] as MultiVariantsFeatureMetadata;

    if (featureMetadata.target === Target.NEW_USERS) {
      const isNew = await isNewUser();
      if (!isNew) return 'excludedNotLogged' as any;
    }

    return _getAndLogVariant(key, options as OptionsType<K>);
  };

  const useFeatureVariantForNewUsers = <K extends FeatureKeysTargetingNewUsers>(
    key: K,
    { isNewUser, ...options }: Options<AllVariantsOf<K>> & { isNewUser: () => Promise<boolean> },
  ): UseFeatureVariantReturnType<AllVariantsOf<K> | 'excludedNotLogged'> => {
    const [status, setStatus] = useState<UseFeatureVariantReturnType<AllVariantsOf<K> | 'excludedNotLogged'>>({
      isLoading: true,
    });

    useEffect(() => {
      const fetchVariant = async () => {
        const featureMetadata = featureDefinitions[key] as MultiVariantsFeatureMetadata;

        if (featureMetadata.target === Target.NEW_USERS) {
          const isNew = await isNewUser();
          if (!isNew) {
            setStatus({ isLoading: false, variant: 'excludedNotLogged' });
            return;
          }
        }
        const variant = await _getAndLogVariant(key, options as OptionsType<K>);
        setStatus({ isLoading: false, variant });
      };
      fetchVariant();
    }, [key]);

    return status;
  };

  return {
    getFeatureVariant,
    useFeatureVariant,
    isFeatureVariantReady,
    useFeatureVariantForNewUsers,
    getFeatureVariantForNewUsers,
  };
}
